<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useViewOnly } from '@/composables/useViewOnly'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'outline-light' | 'plain-light' | 'invert' | 'ghost' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    to?: string
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    /**
     * Opts a button out of the automatic view-only disabling below — for
     * the small minority of BaseButtons that don't write anything (e.g.
     * "Export PDF Report"). Every other non-navigation BaseButton in the
     * admin/staff UI is assumed to trigger a mutation and is disabled by
     * default for the read-only `admin-viewer` role, since auditing every
     * call site individually isn't worth it when defaulting to "disabled"
     * fails safe and the real write-blocking guarantee is server-side
     * anyway (see useViewOnly's own comment).
     */
    viewOnlySafe?: boolean
  }>(),
  {
    variant: 'primary',
    size: 'md',
    to: undefined,
    type: 'button',
    disabled: false,
    loading: false,
    viewOnlySafe: false,
  },
)

defineEmits<{ click: [MouseEvent] }>()

const isRoute = computed(() => !!props.to)
const { isViewOnly } = useViewOnly()
const isDisabled = computed(
  () => props.disabled || props.loading || (isViewOnly.value && !isRoute.value && !props.viewOnlySafe),
)

const variantClass = computed(() => {
  switch (props.variant) {
    case 'secondary':
      return 'bg-transparent text-navy border-[1.5px] border-navy hover:bg-navy/5'
    case 'outline-light':
      return 'bg-transparent text-cream border-[1.5px] border-cream/55 hover:bg-cream/10'
    case 'plain-light':
      return 'bg-transparent text-cream hover:opacity-80'
    case 'invert':
      return 'bg-navy text-cream hover:bg-navy-deep'
    case 'ghost':
      return 'bg-transparent text-navy hover:bg-navy/10'
    case 'danger':
      return 'bg-red-600 text-cream hover:bg-red-700'
    default:
      return 'bg-lime text-navy hover:bg-lime/90'
  }
})

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'px-3 py-1.5 text-xs'
    case 'lg':
      return 'px-8 py-3.75 text-base'
    default:
      return 'px-5 py-2.5 text-sm'
  }
})
</script>

<template>
  <component
    :is="isRoute ? RouterLink : 'button'"
    :to="isRoute ? to : undefined"
    :type="!isRoute ? type : undefined"
    :disabled="!isRoute && isDisabled"
    class="inline-flex items-center justify-center gap-2 rounded-full font-primary font-extrabold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    :class="[variantClass, sizeClass]"
    @click="$emit('click', $event)"
  >
    <span
      v-if="loading"
      class="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
    <slot />
  </component>
</template>

<style scoped></style>
